/**
 * Attio adapter.
 *
 * API base: https://api.attio.com/v2/
 * Auth: Bearer {access_token}
 *
 * Attio's model is generic ("objects" with arbitrary "attributes"), so we target the
 * default `deals` object. Attribute slugs we read:
 *   name, value, stage, owner, associated_company, associated_people,
 *   created_at, updated_at
 *
 * Pagination: Attio uses `offset` + `limit` on /records/query. We surface offset as
 * `nextCursor` (stringified). When the returned page is shorter than the requested
 * limit, nextCursor is null.
 *
 * Attio doesn't push webhooks for everything — Phase 4 will schedule a polling
 * function. This adapter exposes the same shape so the polling job can call it.
 */

import {
  type CrmAdapter,
  AdapterError,
  classifyHttpError,
} from './adapter';
import { getProxyAuthHeader } from './proxy-auth';
import type {
  Contact,
  CreateContactInput,
  CreateDealInput,
  CrmFilter,
  CrmMeeting,
  CrmObjectType,
  CustomField,
  Deal,
  ListContactsOpts,
  ListContactsResult,
  ListDealsOpts,
  ListDealsResult,
  ListLeadsOpts,
  ListLeadsResult,
  ListMeetingsOpts,
  ListMeetingsResult,
  Owner,
  UpdateContactInput,
  UpdateDealPatch,
} from './types';

const ATTIO_API_BASE = 'https://api.attio.com/v2';

type AttioTransport =
  | { mode: 'proxy' }
  | { mode: 'direct'; accessToken: string };

/** Attio attribute values are nested arrays — see https://developers.attio.com */
type AttioAttributeValue = Array<Record<string, unknown>>;

interface AttioRecord {
  id: {
    workspace_id: string;
    object_id: string;
    record_id: string;
  };
  values: Record<string, AttioAttributeValue>;
  created_at?: string;
  updated_at?: string;
}

interface AttioListResponse<T> {
  data: T[];
}

interface AttioAttribute {
  api_slug: string;
  title: string;
  type: string;
  config?: {
    options?: { title: string; value?: string }[];
  };
}

interface AttioWorkspaceMember {
  id: { workspace_member_id: string };
  email_address: string;
  first_name: string | null;
  last_name: string | null;
}

interface AttioList {
  id: { list_id: string };
  name: string;
  parent_object: string;
}

export class AttioAdapter implements CrmAdapter {
  readonly type = 'attio' as const;
  private transport: AttioTransport;

  constructor(transport: AttioTransport = { mode: 'proxy' }) {
    this.transport = transport;
  }

  async listDeals(opts: ListDealsOpts = {}): Promise<ListDealsResult> {
    const limit = opts.pageSize ?? 25;
    const offset = opts.cursor ? Number(opts.cursor) : 0;
    const body: Record<string, unknown> = {
      limit,
      offset,
      sorts: [{ attribute: 'created_at', direction: 'desc' }],
    };
    if (opts.filterId) {
      // Attio "filterId" maps to a saved list — query against the list's records endpoint instead.
      const raw = await this.call<AttioListResponse<AttioRecord>>(
        'POST',
        `/lists/${opts.filterId}/entries/query`,
        body,
      );
      const items = raw.data.map((r) => this.normaliseDeal(r));
      return {
        items,
        nextCursor: raw.data.length < limit ? null : String(offset + raw.data.length),
      };
    }

    const raw = await this.call<AttioListResponse<AttioRecord>>(
      'POST',
      '/objects/deals/records/query',
      body,
    );
    const items = raw.data.map((r) => this.normaliseDeal(r));
    return {
      items,
      nextCursor: raw.data.length < limit ? null : String(offset + raw.data.length),
    };
  }

  async listOwners(): Promise<Owner[]> {
    const raw = await this.call<AttioListResponse<AttioWorkspaceMember>>(
      'GET',
      '/workspace_members',
    );
    return raw.data.map((m) => ({
      id: m.id.workspace_member_id,
      email: m.email_address,
      firstName: m.first_name ?? '',
      lastName: m.last_name ?? '',
    }));
  }

  async listFilters(): Promise<CrmFilter[]> {
    // Attio "lists" are saved filtered views of an object.
    const raw = await this.call<AttioListResponse<AttioList>>('GET', '/lists');
    return raw.data
      .filter((l) => l.parent_object === 'deals' || l.parent_object === 'people')
      .map((l) => ({
        id: l.id.list_id,
        name: l.name,
        objectType: (l.parent_object === 'deals' ? 'deal' : 'contact') as CrmObjectType,
      }));
  }

  async listCustomFields(objectType: CrmObjectType): Promise<CustomField[]> {
    const slug = objectType === 'deal' ? 'deals' : 'people';
    const raw = await this.call<AttioListResponse<AttioAttribute>>(
      'GET',
      `/objects/${slug}/attributes`,
    );
    return raw.data.map((a) => ({
      name: a.api_slug,
      label: a.title,
      type: this.mapAttributeType(a.type),
      options: a.config?.options?.map((o) => o.value ?? o.title),
    }));
  }

  async getDeal(id: string): Promise<Deal | null> {
    try {
      const raw = await this.call<{ data: AttioRecord }>(
        'GET',
        `/objects/deals/records/${id}`,
      );
      return this.normaliseDeal(raw.data);
    } catch (err) {
      if (err instanceof AdapterError && err.code === 'NOT_FOUND') return null;
      throw err;
    }
  }

  async createDeal(input: CreateDealInput): Promise<Deal> {
    const values: Record<string, unknown> = {
      name: [{ value: input.name }],
    };
    if (typeof input.amount === 'number') {
      values.value = [{ currency_value: input.amount, currency_code: 'USD' }];
    }
    if (input.stageId) {
      values.stage = [{ status: { id: input.stageId } }];
    }
    if (input.customFields) {
      for (const [k, v] of Object.entries(input.customFields)) {
        if (v === null || v === undefined) continue;
        values[k] = [{ value: v }];
      }
    }
    const raw = await this.call<{ data: AttioRecord }>(
      'POST',
      '/objects/deals/records',
      { data: { values } },
    );
    return this.normaliseDeal(raw.data);
  }

  async updateDeal(id: string, patch: UpdateDealPatch): Promise<Deal> {
    const values: Record<string, unknown> = {};
    if (patch.name !== undefined) values.name = [{ value: patch.name }];
    if (patch.amount !== undefined) {
      values.value = [{ currency_value: patch.amount, currency_code: 'USD' }];
    }
    if (patch.stageId !== undefined) values.stage = [{ status: { id: patch.stageId } }];

    const raw = await this.call<{ data: AttioRecord }>(
      'PATCH',
      `/objects/deals/records/${id}`,
      { data: { values } },
    );
    return this.normaliseDeal(raw.data);
  }

  async assignOwner(dealId: string, ownerId: string): Promise<void> {
    await this.call<{ data: AttioRecord }>(
      'PATCH',
      `/objects/deals/records/${dealId}`,
      {
        data: {
          values: {
            owner: [{ referenced_actor_type: 'workspace-member', referenced_actor_id: ownerId }],
          },
        },
      },
    );
  }

  async listMeetings(opts: ListMeetingsOpts = {}): Promise<ListMeetingsResult> {
    const limit = opts.pageSize ?? 100;
    const fromMs = opts.fromIso ? Date.parse(opts.fromIso) : Number.NEGATIVE_INFINITY;
    const toMs = opts.toIso ? Date.parse(opts.toIso) : Number.POSITIVE_INFINITY;

    // Try the optional `meetings` object first. Attio workspaces only have it
    // when the customer has configured one — most won't, so we silently fall
    // back to "deals with an upcoming close_date" which is the closest analogue.
    try {
      const raw = await this.call<AttioListResponse<AttioRecord>>(
        'POST',
        '/objects/meetings/records/query',
        {
          limit,
          offset: 0,
          sorts: [{ attribute: 'created_at', direction: 'desc' }],
        },
      );
      const items: CrmMeeting[] = raw.data
        .map((r) => this.normaliseMeeting(r))
        .filter((m): m is CrmMeeting => m !== null)
        .filter((m) => {
          const s = Date.parse(m.startAt);
          return s >= fromMs && s <= toMs;
        });
      return { items };
    } catch (err) {
      if (!(err instanceof AdapterError) || (err.code !== 'NOT_FOUND' && err.code !== 'VALIDATION_FAILED')) {
        throw err;
      }
      // Fall through to deals-as-meetings fallback.
    }

    const fallback = await this.call<AttioListResponse<AttioRecord>>(
      'POST',
      '/objects/deals/records/query',
      {
        limit,
        offset: 0,
        sorts: [{ attribute: 'created_at', direction: 'desc' }],
      },
    );
    const items: CrmMeeting[] = fallback.data
      .map((r) => this.dealAsMeeting(r))
      .filter((m): m is CrmMeeting => m !== null)
      .filter((m) => {
        const s = Date.parse(m.startAt);
        return s >= fromMs && s <= toMs;
      });
    return { items };
  }

  /** Read an Attio `meetings` record into our canonical CrmMeeting shape. */
  private normaliseMeeting(r: AttioRecord): CrmMeeting | null {
    const v = r.values ?? {};
    const title = readScalar<string>(v.title, 'value') ?? readScalar<string>(v.name, 'value') ?? '(untitled meeting)';
    const startRaw =
      readScalar<string>(v.start_at, 'value') ??
      readScalar<string>(v.starts_at, 'value') ??
      readScalar<string>(v.scheduled_at, 'value') ??
      r.created_at;
    if (!startRaw) return null;
    const startMs = Date.parse(startRaw);
    if (!Number.isFinite(startMs)) return null;
    const endRaw =
      readScalar<string>(v.end_at, 'value') ??
      readScalar<string>(v.ends_at, 'value');
    const endMs = endRaw && Number.isFinite(Date.parse(endRaw))
      ? Date.parse(endRaw)
      : startMs + 30 * 60_000;
    const location = readScalar<string>(v.location, 'value') ?? null;
    return {
      id: r.id.record_id,
      title,
      startAt: new Date(startMs).toISOString(),
      endAt: new Date(endMs).toISOString(),
      location,
      attendees: [] as string[],
      url: null,
      raw: { ...v } as Record<string, unknown>,
    };
  }

  /** Fallback: project a deal's close_date into the CrmMeeting shape. */
  private dealAsMeeting(r: AttioRecord): CrmMeeting | null {
    const v = r.values ?? {};
    const closeRaw =
      readScalar<string>(v.close_date, 'value') ??
      readScalar<string>(v.closeDate, 'value');
    if (!closeRaw) return null;
    const startMs = Date.parse(closeRaw);
    if (!Number.isFinite(startMs)) return null;
    const name = readScalar<string>(v.name, 'value') ?? '(deal)';
    return {
      id: r.id.record_id,
      title: `Deal close: ${name}`,
      startAt: new Date(startMs).toISOString(),
      endAt: new Date(startMs + 30 * 60_000).toISOString(),
      location: null,
      attendees: [] as string[],
      url: null,
      raw: { ...v, _fallbackFrom: 'deal' } as Record<string, unknown>,
    };
  }

  // ── Contact CRUD ─────────────────────────────────────────────────────────

  async listContacts(opts: ListContactsOpts = {}): Promise<ListContactsResult> {
    const limit = opts.limit ?? 25;
    const offset = opts.cursor ? Number(opts.cursor) : 0;
    const raw = await this.call<AttioListResponse<AttioRecord>>(
      'POST',
      '/objects/people/records/query',
      {
        limit,
        offset,
        sorts: [{ attribute: 'created_at', direction: 'desc' }],
      },
    );
    const items = raw.data.map((r) => this.normaliseContact(r));
    return {
      items,
      nextCursor: raw.data.length < limit ? null : String(offset + raw.data.length),
    };
  }

  async getContact(id: string): Promise<Contact | null> {
    try {
      const raw = await this.call<{ data: AttioRecord }>(
        'GET',
        `/objects/people/records/${id}`,
      );
      return this.normaliseContact(raw.data);
    } catch (err) {
      if (err instanceof AdapterError && err.code === 'NOT_FOUND') return null;
      throw err;
    }
  }

  async createContact(input: CreateContactInput): Promise<Contact> {
    const values = this.contactInputToValues(input);
    const raw = await this.call<{ data: AttioRecord }>(
      'POST',
      '/objects/people/records',
      { data: { values } },
    );
    return this.normaliseContact(raw.data);
  }

  async updateContact(id: string, patch: UpdateContactInput): Promise<Contact> {
    const values = this.contactInputToValues(patch);
    const raw = await this.call<{ data: AttioRecord }>(
      'PATCH',
      `/objects/people/records/${id}`,
      { data: { values } },
    );
    return this.normaliseContact(raw.data);
  }

  async listLeads(opts: ListLeadsOpts = {}): Promise<ListLeadsResult> {
    const limit = opts.limit ?? 25;
    const offset = opts.cursor ? Number(opts.cursor) : 0;
    const stages = opts.stages;
    // Attio lacks native lifecycle filtering. Fetch the page and filter client-side
    // to records whose lifecycleStage is null OR in the desired stages.
    const raw = await this.call<AttioListResponse<AttioRecord>>(
      'POST',
      '/objects/people/records/query',
      {
        limit,
        offset,
        sorts: [{ attribute: 'created_at', direction: 'desc' }],
      },
    );
    let items = raw.data.map((r) => this.normaliseContact(r));
    // Client-side filter: keep records where lifecycleStage is null OR in stages.
    // When stages is undefined/empty, return all (workspace doesn't have lifecycle attribute).
    if (stages && stages.length > 0) {
      items = items.filter(
        (c) => c.lifecycleStage === null || stages.includes(c.lifecycleStage),
      );
    }
    return {
      items,
      nextCursor: raw.data.length < limit ? null : String(offset + raw.data.length),
    };
  }

  /**
   * Attio's `people` object stores `name` as a structured `{ first_name, last_name, full_name }`
   * record and `email_addresses` / `phone_numbers` as arrays. We accept the flat
   * Contact shape and map to the typed Attio write format.
   */
  private contactInputToValues(input: CreateContactInput | UpdateContactInput): Record<string, unknown> {
    const values: Record<string, unknown> = {};
    if (input.firstName !== undefined || input.lastName !== undefined) {
      values.name = [
        {
          first_name: input.firstName ?? '',
          last_name: input.lastName ?? '',
          full_name: [input.firstName, input.lastName].filter(Boolean).join(' '),
        },
      ];
    }
    if (input.email !== undefined) {
      values.email_addresses = input.email
        ? [{ email_address: input.email }]
        : [];
    }
    if (input.phone !== undefined) {
      values.phone_numbers = input.phone
        ? [{ original_phone_number: input.phone }]
        : [];
    }
    if (input.company !== undefined) {
      // `company` lands as a string on the flat Contact; in Attio `company` is
      // a reference attribute. For now we write a free-form string into
      // `job_title`-adjacent shape via `company_name` custom field when present.
      // Customers wiring a real reference should override via customFields.
      values.company_name = input.company ? [{ value: input.company }] : [];
    }
    if (input.customFields) {
      for (const [k, v] of Object.entries(input.customFields)) {
        if (v === null || v === undefined) continue;
        values[k] = Array.isArray(v) ? v : [{ value: v }];
      }
    }
    return values;
  }

  private normaliseContact(r: AttioRecord): Contact {
    const v = r.values ?? {};
    // Attio name attr can be an array of structured objects.
    const firstName =
      readScalar<string>(v.name, 'first_name') ??
      readScalar<string>(v.first_name, 'value') ??
      null;
    const lastName =
      readScalar<string>(v.name, 'last_name') ??
      readScalar<string>(v.last_name, 'value') ??
      null;
    // email_addresses[0].email_address — Attio v2 shape.
    const email =
      readScalar<string>(v.email_addresses, 'email_address') ??
      readScalar<string>(v.email_addresses, 'value') ??
      readScalar<string>(v.email, 'value') ??
      null;
    const phone =
      readScalar<string>(v.phone_numbers, 'original_phone_number') ??
      readScalar<string>(v.phone_numbers, 'phone_number') ??
      readScalar<string>(v.phone_numbers, 'value') ??
      readScalar<string>(v.phone, 'value') ??
      null;
    const company =
      readScalar<string>(v.company, 'value') ??
      readScalar<string>(v.company_name, 'value') ??
      readNested<string>(v.company, 'target_record_id', 'value') ??
      null;
    // Lifecycle stage — try lead_status or lifecycle_stage; null if workspace lacks attribute.
    const lifecycleStage =
      readScalar<string>(v.lead_status, 'value') ??
      readScalar<string>(v.lifecycle_stage, 'value') ??
      null;
    // Address composition — primary_location { line_1, locality, postcode }.
    const addressParts = [
      readScalar<string>(v.primary_location, 'line_1'),
      readScalar<string>(v.primary_location, 'locality'),
      readScalar<string>(v.primary_location, 'postcode'),
    ].filter(Boolean);
    const address = addressParts.length > 0 ? addressParts.join(', ') : null;

    const known = new Set([
      'name',
      'first_name',
      'last_name',
      'email_addresses',
      'email',
      'phone_numbers',
      'phone',
      'company',
      'company_name',
      'lead_status',
      'lifecycle_stage',
      'primary_location',
      'created_at',
      'updated_at',
    ]);
    const customFields: Record<string, unknown> = {};
    for (const [k, val] of Object.entries(v)) {
      if (!known.has(k)) customFields[k] = val;
    }
    // Preserve full array shape for emails / phones — caller may want all of them.
    if (Array.isArray(v.email_addresses) && v.email_addresses.length > 1) {
      customFields._email_addresses = v.email_addresses;
    }
    if (Array.isArray(v.phone_numbers) && v.phone_numbers.length > 1) {
      customFields._phone_numbers = v.phone_numbers;
    }

    return {
      id: r.id.record_id,
      firstName,
      lastName,
      email,
      phone,
      company,
      lifecycleStage,
      address,
      customFields,
      createdAt: new Date(r.created_at ?? Date.now()),
      updatedAt: new Date(r.updated_at ?? Date.now()),
    };
  }

  async setCustomField(dealId: string, fieldName: string, value: unknown): Promise<void> {
    await this.call<{ data: AttioRecord }>(
      'PATCH',
      `/objects/deals/records/${dealId}`,
      { data: { values: { [fieldName]: [{ value }] } } },
    );
  }

  // ── Helpers ──────────────────────────────────────────────────────────────

  private normaliseDeal(r: AttioRecord): Deal {
    const v = r.values ?? {};

    const name = readScalar<string>(v.name, 'value') ?? '';
    const amount = readScalar<number>(v.value, 'currency_value');
    const stageId =
      readNested<string>(v.stage, 'status', 'id') ??
      readScalar<string>(v.stage, 'status_id') ??
      null;
    const stageName =
      readNested<string>(v.stage, 'status', 'title') ??
      readScalar<string>(v.stage, 'value') ??
      null;
    const ownerId =
      readScalar<string>(v.owner, 'referenced_actor_id') ??
      readScalar<string>(v.owner, 'workspace_member_id') ??
      null;

    // Address composition — try a "primary_address" attribute if it exists.
    const addrParts = [
      readScalar<string>(v.primary_address, 'line_1'),
      readScalar<string>(v.primary_address, 'locality'),
      readScalar<string>(v.primary_address, 'region'),
      readScalar<string>(v.primary_address, 'postcode'),
    ].filter(Boolean);
    const address = addrParts.length > 0 ? addrParts.join(', ') : null;

    // First associated person → contactName/phone (best effort).
    const contactName = readNested<string>(v.associated_people, 'target_record_id', 'value') ?? null;

    const known = new Set([
      'name',
      'value',
      'stage',
      'owner',
      'created_at',
      'updated_at',
      'primary_address',
      'associated_people',
      'associated_company',
    ]);
    const customFields: Record<string, unknown> = {};
    for (const [k, val] of Object.entries(v)) {
      if (!known.has(k)) customFields[k] = val;
    }

    return {
      id: r.id.record_id,
      name,
      amount: typeof amount === 'number' ? amount : null,
      stageId,
      stageName,
      ownerId,
      address,
      contactName,
      contactPhone: null,
      customFields,
      createdAt: new Date(r.created_at ?? Date.now()),
      updatedAt: new Date(r.updated_at ?? Date.now()),
    };
  }

  private mapAttributeType(t: string): CustomField['type'] {
    if (t === 'number' || t === 'currency' || t === 'rating') return 'number';
    if (t === 'select' || t === 'status') return 'enum';
    if (t === 'date' || t === 'timestamp') return 'date';
    return 'string';
  }

  // ── HTTP transport ───────────────────────────────────────────────────────

  private async call<T>(method: 'GET' | 'POST' | 'PATCH', path: string, body?: unknown): Promise<T> {
    if (this.transport.mode === 'direct') {
      return this.callDirect<T>(method, path, body, this.transport.accessToken);
    }
    return this.callProxy<T>(method, path, body);
  }

  private async callDirect<T>(
    method: 'GET' | 'POST' | 'PATCH',
    path: string,
    body: unknown,
    accessToken: string,
  ): Promise<T> {
    let res: Response;
    try {
      res = await fetch(`${ATTIO_API_BASE}${path}`, {
        method,
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'content-type': 'application/json',
        },
        body: body ? JSON.stringify(body) : undefined,
      });
    } catch (err) {
      throw new AdapterError('NETWORK_ERROR', `[attio] fetch failed: ${(err as Error).message}`);
    }
    if (!res.ok) {
      const text = await safeText(res);
      throw new AdapterError(classifyHttpError(res.status, text), `[attio] ${res.status} ${path}`, {
        status: res.status,
        upstreamBody: text,
      });
    }
    if (res.status === 204) return undefined as T;
    return (await res.json()) as T;
  }

  private async callProxy<T>(
    method: 'GET' | 'POST' | 'PATCH',
    path: string,
    body: unknown,
  ): Promise<T> {
    const authHeader = await getProxyAuthHeader();
    let res: Response;
    try {
      res = await fetch('/.netlify/functions/crm-proxy', {
        method: 'POST',
        headers: { 'content-type': 'application/json', ...authHeader },
        body: JSON.stringify({ provider: 'attio', method, path, body }),
      });
    } catch (err) {
      throw new AdapterError('NETWORK_ERROR', `[attio-proxy] ${(err as Error).message}`);
    }
    if (!res.ok) {
      const text = await safeText(res);
      throw new AdapterError(
        res.status === 401 ? 'NOT_AUTHENTICATED' : classifyHttpError(res.status, text),
        `[attio-proxy] ${res.status}`,
        { status: res.status, upstreamBody: text },
      );
    }
    return (await res.json()) as T;
  }
}

/** Read a scalar field from Attio's nested `[{...}]` attribute shape. */
function readScalar<T>(attr: AttioAttributeValue | undefined, key: string): T | undefined {
  if (!attr || attr.length === 0) return undefined;
  const v = attr[0]?.[key];
  return v === undefined ? undefined : (v as T);
}

function readNested<T>(
  attr: AttioAttributeValue | undefined,
  k1: string,
  k2: string,
): T | undefined {
  if (!attr || attr.length === 0) return undefined;
  const inner = attr[0]?.[k1];
  if (inner == null || typeof inner !== 'object') return undefined;
  return (inner as Record<string, unknown>)[k2] as T | undefined;
}

async function safeText(res: Response): Promise<string> {
  try {
    return await res.text();
  } catch {
    return '';
  }
}
